import { z } from 'zod';
import { rolSchema } from './roles.js';
/** Tipos de magic link. Cada uno tiene su flujo y su pantalla. */
export const tipoMagicLinkSchema = z.enum([
    /** Alta del paciente: el médico lo da de alta y el sistema manda el enlace (P-01). */
    'ONBOARDING_PACIENTE',
    /** Invitación al cuidador: C-00 invitación → C-01 aceptar o rechazar. */
    'ACTIVACION_CUIDADOR',
    /** Primer acceso del equipo, que después entra con email y contraseña (A-11). */
    'PRIMER_ACCESO_EQUIPO',
    /**
     * Volver a entrar, para el paciente o el cuidador que ya pasó por su primer
     * enlace y perdió el dispositivo recordado (otro teléfono, datos borrados, 90
     * días cumplidos). Lo emite el equipo desde la ficha (A04).
     */
    'REINGRESO',
]);
const DIA_EN_MINUTOS = 24 * 60;
/**
 * Cuánto vive cada enlace, en minutos. **El único lugar donde se decide.**
 *
 * El primer enlace del paciente y la invitación al cuidador llegan a una
 * persona que puede tardar días en abrirlos: un adulto mayor recién operado no
 * está mirando el teléfono. El reingreso, en cambio, lo pide alguien que quiere
 * entrar ahora, y un enlace que vale para entrar no tiene por qué quedar vivo
 * más que eso. El primer acceso del equipo conserva los 30 minutos de siempre:
 * no hay todavía quién lo emita (gestión de usuarios, parte 2).
 */
export const VIDA_DEL_ENLACE_EN_MINUTOS = Object.freeze({
    ONBOARDING_PACIENTE: 7 * DIA_EN_MINUTOS,
    ACTIVACION_CUIDADOR: 7 * DIA_EN_MINUTOS,
    PRIMER_ACCESO_EQUIPO: 30,
    REINGRESO: 30,
});
/**
 * Cuánto dura el dispositivo recordado del paciente y del cuidador **sin usarse**
 * (INGRESO-1, ADR-024). Es una ventana deslizante: cada renovación lo corre a 90
 * días desde ese momento, así que un teléfono que se abre seguido no vence nunca
 * dentro del programa. Vence sólo si pasan 90 días sin abrir la app, o al llegar
 * al tope (`DIAS_DE_GRACIA_DEL_DISPOSITIVO`).
 */
export const DIAS_SIN_USO_DEL_DISPOSITIVO = 90;
/**
 * Los días que el dispositivo sigue valiendo después del último día del
 * programa (365, más la extensión): el margen para que el cierre se haga sin
 * que el paciente quede afuera. Nunca vence después de eso, aunque se use.
 */
export const DIAS_DE_GRACIA_DEL_DISPOSITIVO = 30;
/**
 * Cuánto vive el código de activación (INGRESO-2, ADR-024 punto 11): diez
 * minutos. Alcanza para pedirlo en Safari, agregar la app a la pantalla de
 * inicio, abrirla y escribirlo, y es corto porque seis dígitos se adivinan
 * más fácil que un enlace: cada minuto de vida es un minuto en el que alguien
 * puede probar.
 */
export const VIDA_DEL_CODIGO_DE_ACTIVACION_EN_MINUTOS = 10;
/** Seis dígitos: los que una persona mayor copia sin perderse. */
export const codigoDeActivacionSchema = z.string().regex(/^\d{6}$/);
/** Lo que manda la app instalada para canjear su código. Viaja en el body. */
export const canjearCodigoDeActivacionSchema = z.strictObject({
    codigo: codigoDeActivacionSchema,
});
/**
 * Lo que devuelve pedir un código: el código, sólo a quien lo pidió, y cuándo
 * vence. La pantalla no cuenta los segundos: usa el vencimiento para saber
 * cuándo decir que venció.
 */
export const codigoDeActivacionEmitidoSchema = z.strictObject({
    codigo: codigoDeActivacionSchema,
    venceAt: z.iso.datetime(),
});
/**
 * Cuerpo de la validación del magic link.
 *
 * El token viaja en el body y no en la querystring: `pii-handling.md` §2 prohíbe
 * datos sensibles en URLs, y un token en una URL queda en el historial del
 * navegador, en el `Referer` y en cualquier log intermedio. La pantalla lo lee
 * del enlace y lo manda por POST.
 */
export const validarMagicLinkSchema = z.object({
    token: z.string().min(32).max(256),
});
/** Respuesta de una validación exitosa. */
export const sesionCreadaSchema = z.object({
    /**
     * ID token de Identity Platform, ya canjeado por el backend.
     *
     * El canje lo hace el servidor y no el navegador (`auth/id-token.ts`): así el
     * dispositivo del paciente sólo habla con nuestro dominio y la PWA no lleva
     * SDK de autenticación ni API key embebida.
     */
    idToken: z.string().min(1),
    /** Segundos de vida del ID token, para que el cliente sepa cuándo renovarlo. */
    expiraEnSegundos: z.number().int().positive(),
    rol: rolSchema,
    tipo: tipoMagicLinkSchema,
    /** Minutos de inactividad tras los cuales la sesión expira (S-09). */
    expiraPorInactividadEnMinutos: z.number().int().positive(),
});
/**
 * Respuesta de una renovación por dispositivo recordado.
 *
 * Es la misma sesión que la del enlace, sin el tipo de enlace: acá no hubo
 * ninguno.
 */
export const sesionRenovadaSchema = sesionCreadaSchema.omit({ tipo: true });
/** Decisión del cuidador en C-01. */
export const decisionCuidadorSchema = z.object({
    token: z.string().min(32).max(256),
    acepta: z.boolean(),
});
/**
 * Errores de autenticación que el cliente puede ver.
 *
 * La lista es corta y deliberadamente poco informativa: un 401 no dice si el
 * usuario existe, si el token venció o si nunca fue válido. Distinguirlos le
 * sirve más a quien prueba tokens que a quien tiene uno bueno.
 */
export const errorAuthSchema = z.enum([
    'no_autenticado',
    'sin_permiso',
    'sesion_expirada',
    'enlace_invalido',
    'demasiados_intentos',
    'credenciales_invalidas',
    'codigo_invalido',
    'sin_dispositivo',
]);
export const respuestaErrorSchema = z.object({ error: errorAuthSchema });
// ─────────────────────────────────────────────────────────────────────────────
// Ingreso del equipo · email, contraseña y segundo factor (E3.24)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * El email tal como se compara: minúsculas y sin espacios.
 *
 * La huella de `ingreso_equipo` se calcula sobre esto, así que dos formas de
 * escribir el mismo email tienen que dar lo mismo acá y en ningún otro lado.
 */
export function normalizarEmail(email) {
    return email.replace(/\s+/g, '').toLowerCase();
}
/** Lo que manda la pantalla `/entrar`. Viaja en el body y nunca se loguea. */
export const ingresoEquipoSchema = z.object({
    email: z.string().trim().min(3).max(254).email(),
    contrasena: z.string().min(1).max(4096),
});
/** Un código de la aplicación autenticadora: seis dígitos. */
const codigoTotp = z.string().regex(/^\d{6}$/);
/**
 * Qué pasó con el email y la contraseña.
 *
 * - `sesion`: entró. Los roles sin segundo factor terminan acá.
 * - `segundo_factor`: la contraseña está bien y falta el código. El desafío es
 *   opaco y lo firma el servidor; vuelve con el código y nada más.
 * - `inscribir_segundo_factor`: un administrador que todavía no tiene factor.
 *   El token que viene es **provisorio**: sólo sirve para inscribirlo, y el
 *   resto de la API lo rechaza con 401.
 */
export const resultadoIngresoEquipoSchema = z.discriminatedUnion('estado', [
    z.object({
        estado: z.literal('sesion'),
        sesion: sesionRenovadaSchema,
    }),
    z.object({
        estado: z.literal('segundo_factor'),
        desafio: z.string().min(1),
    }),
    z.object({
        estado: z.literal('inscribir_segundo_factor'),
        tokenProvisorio: z.string().min(1),
    }),
]);
/** El código del segundo factor, contra el desafío que devolvió el ingreso. */
export const segundoFactorSchema = z.object({
    desafio: z.string().min(1).max(8192),
    codigo: codigoTotp,
});
/**
 * Lo que necesita la pantalla para inscribir la aplicación autenticadora.
 *
 * El secreto viaja una sola vez, a la persona que se está inscribiendo, y no
 * se guarda en ningún lado de nuestro lado: vive en Identity Platform. La
 * pantalla lo muestra como QR y como texto; la sesión de inscripción es opaca.
 */
export const inscripcionSegundoFactorSchema = z.object({
    secreto: z.string().min(16),
    uriOtpauth: z.string().startsWith('otpauth://totp/'),
    sesionDeInscripcion: z.string().min(1),
    digitos: z.number().int().positive(),
    periodoSegundos: z.number().int().positive(),
});
export const confirmarInscripcionSchema = z.object({
    sesionDeInscripcion: z.string().min(1).max(8192),
    codigo: codigoTotp,
});
// ─────────────────────────────────────────────────────────────────────────────
// Enlace regenerado desde la ficha (A04)
// ─────────────────────────────────────────────────────────────────────────────
/**
 * El enlace nuevo, para mostrarlo en pantalla mientras no exista WhatsApp.
 *
 * Es la única respuesta de la API que lleva un token de acceso en claro, y la
 * recibe sólo quien tiene `paciente:gestionar`: el equipo se lo entrega a la
 * persona en mano (texto o QR). La URL la arma la pantalla con su propio
 * origen; el token viaja en el fragmento, que no llega a ningún servidor.
 */
export const enlaceRegeneradoSchema = z.object({
    token: z.string().min(32),
    tipo: tipoMagicLinkSchema,
    venceAt: z.string().datetime(),
    anulados: z.number().int().nonnegative(),
});
//# sourceMappingURL=auth.js.map